import React, { useState, useCallback, useEffect } from 'react';
import PostcodeSelect from './PostcodeSelect';
import SwiftcompletePostcodeSelect from './SwiftcompletePostcodeSelect';
import ApiPostcodeSelect from './ApiPostcodeSelect';

/**
 * Which address box to draw is the server's decision, not this component's.
 *
 * `postcodeLookup` is PostcodeLookup.client_props — the provider chosen in
 * config/initializers/postcode_lookup.rb. A `mode` of 'server' means the
 * lookup goes through our own API and one component covers every such
 * provider; 'browser' means a particular vendor's script owns the box, so the
 * provider id picks the component that knows that vendor.
 *
 * The bare fetchifyApiKey/swiftcompleteApiKey props are the older way of
 * saying the same thing, still accepted for callers that have not been
 * updated.
 */
export type PostcodeLookupProps = {
  provider: string;
  mode: 'server' | 'browser';
  configured: boolean;
  apiKey?: string;
};

type AddressValues = {
  line_1?: string;
  line_2?: string;
  city?: string;
  postal_code?: string;
  latitude?: number | null;
  longitude?: number | null;
};

type AddressFieldsProps = AddressValues & {
  onChange: (field: string, value: string) => void;
  namePrefix: string;
};

const FormGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="row mb-3">
    {children}
  </div>
);

const Label: React.FC<{ children: React.ReactNode; htmlFor?: string }> = ({ children, htmlFor }) => (
  <label className="col-sm-3 form-label" htmlFor={htmlFor}>
    {children}
  </label>
);

const Textfield: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <div className="col-sm-9">
    <input type="text" className="form-control" {...props} />
  </div>
);

const AddressFields: React.FC<AddressFieldsProps> = (props) => {
  const { namePrefix, onChange, latitude, longitude } = props;

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const match = e.target.name.match(/\[(.*)\]/);
    if (match) {
      onChange(match[1], e.target.value);
    }
  }, [onChange]);

  const renderField = (label: string, name: string) => {
    const id = namePrefix + "_" + name;
    return (
      <FormGroup>
        <Label htmlFor={namePrefix + '[' + name + ']'}>{label}</Label>
        <Textfield
          name={namePrefix + '[' + name + ']'}
          id={id}
          value={(props as any)[name] || ''}
          onChange={handleChange}
        />
      </FormGroup>
    );
  };

  return (
    <div className="AddressFields">
      {renderField('Address', 'line_1')}
      {renderField('', 'line_2')}
      {renderField('City or town', 'city')}
      {renderField('Postcode', 'postal_code')}
      <div>
        <input type="hidden" value={latitude ?? ''} name={namePrefix + '[latitude]'} />
        <input type="hidden" value={longitude ?? ''} name={namePrefix + '[longitude]'} />
      </div>
    </div>
  );
};

type AddressInputProps = {
  values?: AddressValues;
  namePrefix: string;
  postcodeLookup?: PostcodeLookupProps;
  fetchifyApiKey?: string;
  swiftcompleteApiKey?: string;
  onChange?: (address: AddressValues) => void;
};

const AddressInput: React.FC<AddressInputProps> = ({ values: propValues, namePrefix, postcodeLookup, fetchifyApiKey, swiftcompleteApiKey }) => {
  const hasInitialAddress = propValues && propValues.line_1 && propValues.line_1.trim() !== '';

  const [values, setValues] = useState<AddressValues | undefined>(propValues);
  const [showAddressFields, setShowAddressFields] = useState(!!hasInitialAddress);

  useEffect(() => {
    const hasAddress = propValues && propValues.line_1 && propValues.line_1.trim() !== '';
    setValues(propValues);
    setShowAddressFields(!!hasAddress);
  }, [propValues]);

  const handleSelectAddress = useCallback((address: AddressValues) => {
    setValues(address);
    setShowAddressFields(true);

    window.updateBookingHints?.({
      postcode: address.postal_code,
    });
  }, []);

  const handleEnterManually = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    setValues({
      line_1: '',
      line_2: '',
      city: '',
      postal_code: '',
    });
    setShowAddressFields(true);
  }, []);

  const handleClearAddress = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    setValues({
      line_1: '',
      line_2: '',
      city: '',
      postal_code: '',
      latitude: null,
      longitude: null,
    });
    setShowAddressFields(false);
  }, []);

  const handleChangeAddress = useCallback((field: string, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const renderLookup = () => {
    if (postcodeLookup?.mode === 'server') {
      return <ApiPostcodeSelect onSelect={handleSelectAddress} />;
    }

    if (postcodeLookup?.mode === 'browser') {
      if (postcodeLookup.provider === 'swiftcomplete') {
        return <SwiftcompletePostcodeSelect onSelect={handleSelectAddress} swiftcompleteApiKey={postcodeLookup.apiKey} />;
      }
      return <PostcodeSelect onSelect={handleSelectAddress} fetchifyApiKey={postcodeLookup.apiKey} />;
    }

    // No postcodeLookup prop: an older caller passing a bare key.
    if (swiftcompleteApiKey) {
      return <SwiftcompletePostcodeSelect onSelect={handleSelectAddress} swiftcompleteApiKey={swiftcompleteApiKey} />;
    }
    return <PostcodeSelect onSelect={handleSelectAddress} fetchifyApiKey={fetchifyApiKey} />;
  };

  return (
    <div className="form-horizontal">
      {!showAddressFields &&
        <div className="mb-3 d-flex">
          <label className="form-label col-sm-3">Postcode</label>
           <div className="col-sm-9">
             {renderLookup()}
             <p style={{ marginTop: 10 }}><a href="#" onClick={handleEnterManually}>Enter address manually</a></p>
           </div>
        </div>
      }

      {showAddressFields &&
        <div>
          <AddressFields {...values} namePrefix={namePrefix} onChange={handleChangeAddress} />
          <div className="mb-3">
            <div className="col-sm-offset-3 col-sm-9">
              <a href="#" onClick={handleClearAddress}>Search for a different address</a>
            </div>
          </div>
        </div>
      }
    </div>
  );
};

export default AddressInput;
